import React, { useState, useEffect, useRef } from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import PostRequest from 'components/common/PostRequest.jsx';
import QueuedPostRequest from 'components/layout_and_quote_builder/utils/QueuedPostRequest.js';
import { defaultRequestQueueManager } from 'components/layout_and_quote_builder/utils/RequestQueueManager.js';
import LayoutNavSectionCombinationListList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationListList.jsx';
import ManufacturingLineCard from 'components/layout_and_quote_builder/ManufacturingLineCard.jsx';
import { ProgressProvider } from 'components/layout_and_quote_builder/utils/ProgressContext.jsx';
import ProgressBarOverlay from 'components/layout_and_quote_builder/ProgressBarOverlay.jsx';
import LoadingOverlay from 'components/layout_and_quote_builder/LoadingOverlay.jsx';
import { setInCache, clearCache } from 'components/layout_and_quote_builder/utils/SolutionScoreCache.js';

const machineNotFoundFiltering = true;
const TOP_N_PER_PART = window.topNPerPart || 3;  // Number of best solutions to keep per part (configurable via ENV)

const LayoutNavSectionCombinationList = ({ navSection, index, count, setSelectedList, materialLayoutDimensions, previousSelectedList}) => {
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [manuallyEnabled, setManuallyEnabled] = useState(false)
  const showAllRef = useRef(false)
  const hideMissingMachinesRef = useRef(false)
  const show_more_count = 400;

  const [navSectionCombinations, setNavSectionCombinations] = useState( [ ] )
  const [manufacturingLines, setManufacturingLines] = useState( [ ] )
  const [selectedManufacturingLine, setSelectedManufacturingLine] = useState( null )

  // Progress tracking for parts mode scoring
  const [scoringProgress, setScoringProgress] = useState({ completed: 0, total: 0, percentage: 0 });

  // Only auto-fetch for the first section (previousSelectedList === null)
  // All other sections require manual activation via "Start Working on This Section" button
  const shouldFetch = previousSelectedList === null;

  const lastFetchTime = useRef(0);
  const pollingIntervalRef = useRef(null);

  // Score a single solution via API (uses QueuedPostRequest for batching)
  const fetchSolutionScore = (solution) => {
    return QueuedPostRequest({
      url: `/layouts-nav-section-combination-score`,
      data: {
        formula_options: window.dielineOptions,
        diecutting_result: solution.diecutting_result,
        nav_section_id: solution.nav_section_id,
        count: count,
        id: window.drawingId,
        layout_dimension_id: solution.material_layout_dimension.id
      },
      callback: () => {}  // PostRequest requires callback prop
    });
  };

  // Build Cartesian product of arrays
  const cartesianProduct = (arrays) => {
    return arrays.reduce((accumulator, currentArray) => {
      const result = [];
      accumulator.forEach(existingCombination => {
        currentArray.forEach(item => {
          result.push([...existingCombination, item]);
        });
      });
      return result;
    }, [[]]);
  };

  // Handle parts mode: score unique solutions, pick best N per part, build combinations
  const handlePartsMode = async (parts, solutionsByParts) => {
    // 1. Collect all unique solutions with their parts key
    const allSolutions = [];
    Object.entries(solutionsByParts).forEach(([partsKey, solutions]) => {
      solutions.forEach(solution => {
        allSolutions.push({ ...solution, partsKey });
      });
    });

    if (allSolutions.length === 0) {
      setNavSectionCombinations([]);
      setLoading(false);
      setNotFound(true);
      return;
    }

    // Initialize progress tracking
    const totalSolutions = allSolutions.length;
    let completedCount = 0;
    setScoringProgress({ completed: 0, total: totalSolutions, percentage: 0 });

    // 2. Score all solutions in parallel (QueuedPostRequest handles batching at 3 concurrent)
    const scorePromises = allSolutions.map(solution =>
      fetchSolutionScore(solution)
        .then(scoreData => {
          // Update progress
          completedCount++;
          const percentage = Math.round((completedCount / totalSolutions) * 100);
          setScoringProgress({ completed: completedCount, total: totalSolutions, percentage });

          // Cache the score data for later use by LayoutNavSectionCombination
          // Use diecutting_result.uuid as key since that's what LayoutNavSectionCombination uses for lookup
          setInCache(solution.diecutting_result.uuid, scoreData);
          return { solution, scoreData, success: true };
        })
        .catch(error => {
          // Update progress even on error
          completedCount++;
          const percentage = Math.round((completedCount / totalSolutions) * 100);
          setScoringProgress({ completed: completedCount, total: totalSolutions, percentage });

          console.error('[handlePartsMode] Error scoring solution:', error);
          return { solution, scoreData: null, success: false };
        })
    );

    const results = await Promise.all(scorePromises);

    // Reset progress when done
    setScoringProgress({ completed: 0, total: 0, percentage: 0 });

    // 3. Group scored solutions by parts
    const scoredByParts = {};
    results.forEach(({ solution, scoreData, success }) => {
      if (!success || !scoreData) {
        return;
      }
      if (!scoredByParts[solution.partsKey]) {
        scoredByParts[solution.partsKey] = [];
      }
      scoredByParts[solution.partsKey].push({
        ...solution,
        score: scoreData.score
      });
    });

    // 4. Pick best N per part (sorted by score, ascending = best first)
    // Combined solutions (covering all parts) are not limited; individual parts are limited to TOP_N_PER_PART
    const combinedKey = [...parts].sort().join(",");
    const bestByParts = {};
    Object.entries(scoredByParts).forEach(([partsKey, solutions]) => {
      const sorted = [...solutions].sort((a, b) => a.score - b.score);
      if (partsKey === combinedKey) {
        // Show all combined solutions (no limit)
        bestByParts[partsKey] = sorted;
      } else {
        // Limit individual parts to best N
        bestByParts[partsKey] = sorted.slice(0, TOP_N_PER_PART);
      }
    });

    // 5. Build combinations
    const combinations = [];

    // Add combined solutions (single-item combinations that cover all parts)
    if (bestByParts[combinedKey]) {
      bestByParts[combinedKey].forEach(solution => {
        combinations.push([solution]);
      });
    }

    // Build Cartesian product from individual part solutions (best 3 each)
    const partArrays = parts.map(part => bestByParts[part] || []);
    const allPartsHaveSolutions = partArrays.every(arr => arr.length > 0);

    if (allPartsHaveSolutions) {
      const cartesian = cartesianProduct(partArrays);
      cartesian.forEach(combination => {
        combinations.push(combination);
      });
    }

    // 6. Set combinations for display
    setNavSectionCombinations(combinations);
    setLoading(false);
    setNotFound(combinations.length === 0);
  };

  const pollForResults = (jobId) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      fetch(`/layouts-nav-section-combinations-status/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
        .then(response => response.json())
        .then(data => {
          if (data.status === 'completed') {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;

            // Check for parts mode (new grouped format)
            if (data.has_parts) {
              handlePartsMode(data.parts, data.solutions_by_parts);
              return;
            }

            // Manufacturing-line mode: list the machine configurations as cards. Solutions for a
            // manufacturingLine are searched once it is selected (Slice 2).
            if (data.has_manufacturing_lines) {
              setManufacturingLines(() => data.manufacturing_lines || []);
              setLoading(() => false);
              setNotFound(() => (data.manufacturing_lines || []).length === 0);
              return;
            }

            // Standard mode (existing behavior)
            setNavSectionCombinations(() => data.solutions);
            setLoading(() => false);
            setNotFound(() => data.solutions.length === 0);

            if (data.auto_select) {
              setSelectedList(data.solutions[0]);
            }
          } else if (data.status === 'failed') {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;

            setLoading(() => false);
            setNotFound(() => true);
            console.error('Job failed:', data.error);
          }
          // If status is 'processing', continue polling
        })
        .catch(error => {
          console.error('Polling error:', error);
        });
    }, 1000);
  };

  const fetchCombinations = () => {
    // Don't fetch if previous section has no selections and user hasn't manually enabled
    if (!shouldFetch && !manuallyEnabled) {
      setLoading(false);
      setNotFound(false);
      return;
    }

    const now = Date.now();
    if (now - lastFetchTime.current < 500) { return; }
    lastFetchTime.current = now;

    // Reset cache and progress at the start of each fetch
    // This ensures stale cached scores are not reused and the progress bar starts fresh
    clearCache();
    defaultRequestQueueManager.clear();  // Drops still-queued requests from the previous fetch (and bumps the progress generation)
    setScoringProgress({ completed: 0, total: 0, percentage: 0 });

    setLoading(() => true);
    setErrorMessage(() => null);

    PostRequest({
      url: `/layouts-nav-section-combinations/${window.drawingId}/${index}/${count}`,
      data: {
        formula_options: window.dielineOptions,
        material_layout_dimensions: materialLayoutDimensions,
        layout_dimension_ids: materialLayoutDimensions.layout_dimension_ids,
        user_quote_id: window.user_quote_id,
        manufacturing_line_identifier: selectedManufacturingLine ? selectedManufacturingLine.identifier : undefined
      },
      callback: (data) => {
        if (data.status === 'failed') {
          // The POST only returns 'failed' for the dev cache-disabled guard,
          // whose `error` is a curated, safe message. Do NOT route the polling
          // path's failure (which carries a raw job exception) through here, or
          // that exception text would leak into the UI.
          setLoading(() => false);
          setErrorMessage(() => data.error);
          return;
        }

        if (data.job_id && data.status === 'processing') {
          pollForResults(data.job_id);
        } else {
          // Synchronous response handling

          // Check for parts mode (new grouped format)
          if (data.has_parts) {
            handlePartsMode(data.parts, data.solutions_by_parts);
            return;
          }

          // Manufacturing-line mode: list the machine configurations as cards. Solutions for a
          // manufacturingLine are searched once it is selected (Slice 2).
          if (data.has_manufacturing_lines) {
            setManufacturingLines(() => data.manufacturing_lines || []);
            setLoading(() => false);
            setNotFound(() => (data.manufacturing_lines || []).length === 0);
            return;
          }

          // Standard mode (existing behavior)
          setNavSectionCombinations(() => data.solutions);
          setLoading(() => false);
          setNotFound(() => data.solutions.length === 0);

          if (data.auto_select) {
            setSelectedList(data.solutions[0]);
          }
        }
      }
    });
  };

  // Cleanup polling interval and cache on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      // Clear score cache to prevent memory leaks when navigating away
      clearCache();
    };
  }, []);

   const sortingInProgress = useRef(false)
   const myRef = useRef();
   const sortDivs = () => {
    if (sortingInProgress.current) return;

    sortingInProgress.current = true;

    var $items = $(myRef.current).children("div");

    let isSorted = true;
    for (let i = 0; i < $items.length - 1; i++) {
        let num_i = parseInt($($items[ i ]).attr("data-score"));
        let num_i_plus_1 = parseInt ($($items[ i+1 ]).attr("data-score"));
        if (num_i > num_i_plus_1 ) {
            isSorted = false;
            break;
        }
    }

    if (!isSorted) {
      $items.sort((a, b) => parseInt($(a).attr("data-score"))  -  parseInt( $(b).attr("data-score") ) );
      $(myRef.current).append($items);
    }

    // Calculate and add star ratings directly to DOM (no React state)
    if ($items.length > 0) {
      const scores = $items.toArray().map(item => parseInt($(item).attr("data-score"))).filter(score => !isNaN(score));
      if (scores.length > 0) {
        const minScore = Math.min(...scores.filter(s => s > 0));
        const maxScore = Math.max(...scores.filter(s => s > 0));
        const range = maxScore - minScore;

        $items.each((index, item) => {
          const score = parseInt($(item).attr("data-score"));
          if (!isNaN(score) && range > 0) {
            const normalizedScore = (score - minScore) / range;
            const starRating = 5 - (normalizedScore * 4); // Scale from 5 to 1 instead of 5 to 0
            const clampedRating = Math.max(1, Math.min(5, starRating)); // Minimum 1 star

            // Round to 1 decimal place to avoid mismatch between displayed text and star rendering
            const roundedRating = Math.round(clampedRating * 10) / 10;

            // Find or create the stars container
            let $starsContainer = $(item).find('.stars-rating-container');
            if ($starsContainer.length === 0) {
              $starsContainer = $('<span class="stars-rating-container"></span>');
              $(item).find('[data-score]').first().parent().prepend($starsContainer);
            }

            // Use manual stars for all ratings for consistent sizing
            const fullStars = Math.floor(roundedRating);
            const remainder = roundedRating - fullStars;
            const hasHalfStar = remainder >= 0.25 && remainder < 0.75;
            const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

            let starsHtml = '<span style="color: #ffc107; font-size: 14px; margin-left: 8px;">';

            // Full stars
            for (let i = 0; i < fullStars; i++) {
              starsHtml += '★';
            }

            // Half star using CSS overlay
            if (hasHalfStar) {
              starsHtml += '<span style="position: relative; display: inline-block;">' +
                '<span style="color: #ddd;">★</span>' +
                '<span style="position: absolute; left: 0; width: 50%; overflow: hidden; color: #ffc107;">★</span>' +
                '</span>';
            }

            // Empty stars
            for (let i = 0; i < emptyStars; i++) {
              starsHtml += '☆';
            }

            starsHtml += ' <span style="margin-left: 4px; color: #666; font-size: 13px;">' + roundedRating.toFixed(1) + '</span></span>';

            $starsContainer.html(starsHtml);
          }
        });
      }
    }

    // Apply visibility after sorting
    if (!myRef.current) {
      sortingInProgress.current = false;
      setTimeout(sortDivs, 200)
      return;
    }

    var $items2 = $(myRef.current).children("div");

    // Count items with missing machines (check for .missing-machine class on children)
    const itemsWithMissingMachines = $items2.filter((_, item) => $(item).find('.missing-machine').length > 0);
    const missingCount = itemsWithMissingMachines.length;

    // Update button visibility and text via jQuery
    const $buttonContainer = $(myRef.current).siblings('.' + styles.show_more_container);
    const $missingMachineButton = $buttonContainer.find('.missing-machine-button');

    if (missingCount > 0) {
      $missingMachineButton.show();
      if (hideMissingMachinesRef.current) {
        $missingMachineButton.text(t('quote_builder.show_missing_machines', { count: missingCount }));
      } else {
        $missingMachineButton.text(t('quote_builder.hide_missing_machines', { count: missingCount }));
      }
    } else {
      $missingMachineButton.hide();
    }

    // Apply visibility based on show more and hide missing machines settings
    let visibleIndex = 0;
    $items2.each((_, item) => {
      const $item = $(item);
      const hasMissingMachine = $item.find('.missing-machine').length > 0;

      // Hide if missing machines filter is active and this item has missing machines
      if (hideMissingMachinesRef.current && hasMissingMachine) {
        $item.addClass(styles.hidden_combination);
        return; // continue to next item
      }

      // Apply show_more_count limit
      if (!showAllRef.current && visibleIndex >= show_more_count) {
        $item.addClass(styles.hidden_combination);
      } else {
        $item.removeClass(styles.hidden_combination);
      }
      visibleIndex++;
    });

    sortingInProgress.current = false;
    setTimeout(sortDivs, 200)
  };

  const showNavSectionCombinationGroup = ( list ) => {
    return <LayoutNavSectionCombinationListList list={list} count={count} setSelectedList={setSelectedList} machineNotFoundFiltering={machineNotFoundFiltering} />
  }

  const randomString = Math.random().toString(36).substring(2, 10);

  // The selected ManufacturingLine's machines for the back button: per machine the name, plus its sheet
  // as the familiar "width X height" shorthand with the localized Width/Height labels (muted) — matching
  // the solution cards. A flexo combined machine is a single row labeled with both roles.
  const selectedManufacturingLineRows = () => {
    const manufacturingLine = selectedManufacturingLine;
    const toDisplay = (valueMm) => (
      window.user_unit == 'in'
        ? `${(parseFloat(valueMm) / 25.4).toFixed(2)}in`
        : `${Math.round(parseFloat(valueMm))}mm`
    );
    const dimensions = (widthMm, heightMm) => {
      if (!widthMm || !heightMm) { return null; }
      return {
        shorthand: `${toDisplay(widthMm)} X ${toDisplay(heightMm)}`,
        labeled: `${t('quote_builder.width_label')}: ${toDisplay(widthMm)}, ${t('quote_builder.height_label')}: ${toDisplay(heightMm)}`
      };
    };

    if (manufacturingLine.printing_and_die_cutting_machine_label) {
      return [{
        label: `${t('layouts.layout_type_die_cutting')} + ${t('layouts.layout_type_printing')}`,
        name: manufacturingLine.printing_and_die_cutting_machine_label,
        dimensions: dimensions(manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm)
      }];
    }

    return [
      { label: t('layouts.layout_type_die_cutting'), name: manufacturingLine.die_cutting_machine_label, dimensions: dimensions(manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm) },
      { label: t('layouts.layout_type_printing'), name: manufacturingLine.printing_machine_label, dimensions: dimensions(manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm) }
    ].filter((row) => Boolean(row.name));
  };

  const renderBackToMachines = () => {
    if (!selectedManufacturingLine) { return null; }

    return (
      <button
        onClick={() => setSelectedManufacturingLine(() => null)}
        className="btn simple-btn"
        style={{ marginBottom: '12px', display: 'inline-flex', alignItems: 'center', textAlign: 'left' }}
      >
        <span style={{ marginRight: '8px', fontSize: '18px' }}>←</span>
        <span>
          {selectedManufacturingLineRows().map((row) => (
            <div key={row.label} style={{ marginBottom: '2px' }}>
              <div><strong>{row.label}:</strong> {row.name}</div>
              {row.dimensions && (
                <div>{row.dimensions.shorthand} <span style={{ opacity: 0.75 }}>({row.dimensions.labeled})</span></div>
              )}
            </div>
          ))}
        </span>
      </button>
    );
  };

  const CombinationsContent = () => {
    const hasMoreThanShowMoreCount = navSectionCombinations.length > show_more_count;

    // Show button if we have more items than show_more_count and showAll is false
    const shouldShowMoreButton = hasMoreThanShowMoreCount && !showAllRef.current;

    const handleToggleMissingMachines = () => {
      hideMissingMachinesRef.current = !hideMissingMachinesRef.current;
      sortDivs();
    };

    // Largest machine sheet dimension across all listed ManufacturingLines — the common scale so the
    // per-machine size rectangles on the cards are comparable to each other (a bigger machine renders bigger).
    const maxMachineDimensionMm = Math.max(1, ...manufacturingLines.flatMap((manufacturingLine) => [
      manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm,
      manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm
    ].map((value) => parseFloat(value) || 0)));

    return (
      <div style={{ position: 'relative' }}>
        <ProgressBarOverlay />
        {renderBackToMachines()}
        <div ref={myRef} className={styles.combinations_grid}>
          {!selectedManufacturingLine && manufacturingLines.length > 0
            ? manufacturingLines.map((manufacturingLine) => (
                <ManufacturingLineCard key={manufacturingLine.identifier} manufacturingLine={manufacturingLine} maxMachineDimensionMm={maxMachineDimensionMm} onSelect={(picked) => setSelectedManufacturingLine(() => picked)} />
              ))
            : navSectionCombinations.map((list, index) => (
                <React.Fragment key={index}>
                  {showNavSectionCombinationGroup(list)}
                </React.Fragment>
              ))}
        </div>

        <div className={styles.show_more_container}>
          {shouldShowMoreButton && (
            <button
              onClick={() => {
                showAllRef.current = true;
                sortDivs();
              }}
              className={`${styles.show_more_button} ${randomString}`}
            >
              {t('quote_builder.show_more')}
            </button>
          )}

          <button
            onClick={handleToggleMissingMachines}
            className={`${styles.show_missing_machines_button} missing-machine-button`}
            style={{ display: 'none' }}
          >
            {t('quote_builder.hide_missing_machines', { count: 0 })}
          </button>
        </div>
      </div>
    );
  };

  // useEffect starts
  useEffect(fetchCombinations, [ count, materialLayoutDimensions, previousSelectedList, manuallyEnabled, selectedManufacturingLine ])
  useEffect(sortDivs, [ ])

  // useEffect ends

  // Check if this section gets its solution from another section
  const userQuoteNavSection = window.user_quote_nav_sections?.find(
    section => section.sort_order === index
  );

  if (userQuoteNavSection?.solution_user_quote_nav_section_id) {
    return null;
  }

  // Show button to enable fetching if shouldFetch is false and not manually enabled
  if (!shouldFetch && !manuallyEnabled) {
    return (
      <div style={{ textAlign: 'center' }}>
        <p> { t('quote_builder.this_section_not_yet_active') } </p>

        <button
          onClick={() => setManuallyEnabled(true)}
          className="btn simple-btn"
        >
          { t('quote_builder.start_working_on_this_section') }
        </button>
        <hr />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div style={{ padding: '12px', color: '#cf3f12', fontWeight: 'bold' }}>
        {errorMessage}
      </div>
    );
  }

  if(notFound) {
    return (
      <div>
        {renderBackToMachines()}
        <div>{t('quote_builder.solution_cannot_be_found')}</div>
      </div>
    )
  }

  // Progress bar for parts mode scoring
  const ScoringProgressBar = () => {
    if (scoringProgress.total === 0) {
      return null;
    }

    return (
      <div className={styles.progressOverlay}>
        <div className={styles.progressCard}>
          <div className={styles.progressTitle}>
            {t('quote_builder.loading')}
          </div>

          <div className={styles.progressBarContainer}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${scoringProgress.percentage}%` }}
            >
              <span className={styles.progressPercentage}>
                {scoringProgress.percentage}%
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ProgressProvider>
      <div>
        {/* Show progress bar during parts mode scoring */}
        <ScoringProgressBar />

        {/* Show loading overlay only when not scoring */}
        {loading && scoringProgress.total === 0 && <LoadingOverlay message={t('quote_builder.loading')} />}

        {!loading && <CombinationsContent />}

      </div>
    </ProgressProvider>
  )
}

export default LayoutNavSectionCombinationList;
